/**
 * Add Broker Account Page (Web)
 * Broker selection grid. Clicking a broker navigates to the step wizard.
 *
 * ⚠️ IMPORTANT: Per ADR-FE-001, this component only orchestrates UI flow.
 * All broker account operations are handled by backend Broker Integration Service.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Avatar,
  Paper,
  Chip,
  Container,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import type { BrokerMetadata } from './types';

/**
 * Maps broker_type to local logo filename
 * Falls back to API logo_url if local logo not found
 */
const getBrokerLogoUrl = (broker: BrokerMetadata): string | undefined => {
  // Map broker types to their logo filenames
  const logoMap: Record<string, string> = {
    zerodha: '/assets/brokers/zerodha-logo.svg',
    angelone: '/assets/brokers/AngelOneLogo.png',
    upstox: '/assets/brokers/upstox-new-logo.svg',
    dhan: '/assets/brokers/Dhan-logo.svg',
    '5paisa': '/assets/brokers/5paisa-logo.svg',
    aliceblue: '/assets/brokers/aliceblue-logo.webp',
    'alice blue': '/assets/brokers/aliceblue-logo.webp',
    'alice blue online': '/assets/brokers/aliceblueonline.png',
    'axis securities': '/assets/brokers/Axis-Securites-logo.png',
    axis: '/assets/brokers/Axis-Securites-logo.png',
    edelweiss: '/assets/brokers/Edelweiss-logo.png',
    fyers: '/assets/brokers/fyers-logo.svg',
    'geojit financial': '/assets/brokers/Geogit Financial.png',
    geojit: '/assets/brokers/Geogit Financial.png',
    groww: '/assets/brokers/groww-logo.svg',
    'hdfc securities': '/assets/brokers/HDFC Securities logo.png',
    hdfc: '/assets/brokers/HDFC Securities logo.png',
    'icici securities': '/assets/brokers/icici-securities-logo.svg',
    icici: '/assets/brokers/icici-securities-logo.svg',
    'iifl capital': '/assets/brokers/iifl-capital-new-logo.png',
    iifl: '/assets/brokers/iifl-capital-new-logo.png',
    'kotak securities': '/assets/brokers/kotak-securities-logo.jpg',
    kotak: '/assets/brokers/kotak-securities-logo.jpg',
    'mirae asset': '/assets/brokers/mirae-asset-sharekhan-logo.png',
    'mirae asset sharekhan': '/assets/brokers/mirae-asset-sharekhan-logo.png',
    sharekhan: '/assets/brokers/mirae-asset-sharekhan-logo.png',
    'motilal oswal': '/assets/brokers/motilal oswal logo.svg',
    'nirmal bang': '/assets/brokers/nirmal bang-logo.png',
    'paytm money': '/assets/brokers/paytm-money-logo.jpg',
    paytm: '/assets/brokers/paytm-money-logo.jpg',
    pml: '/assets/brokers/pml-logo.webp',
    samco: '/assets/brokers/samco-white-logo.svg',
    'sbi securities': '/assets/brokers/sbi-securities-logo.png',
    sbi: '/assets/brokers/sbi-securities-logo.png',
  };

  // Try to find logo by broker_type (case-insensitive)
  const brokerTypeLower = broker.broker_type.toLowerCase();
  const localLogo = logoMap[brokerTypeLower];

  // Return local logo if found, otherwise fall back to API logo_url
  return localLogo || broker.logo_url;
};

export function AddBrokerAccountPage() {
  const navigate = useNavigate();
  const [allBrokers, setAllBrokers] = useState<BrokerMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const fetchBrokers = async () => {
      try {
        setLoading(true);
        setError(null);
        const { api } = await import('@shared/api/client');
        const response = await api.brokerAccountsSupportedBrokers();
        // Store all brokers (both active and non-active)
        setAllBrokers(response.data?.brokers || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch supported brokers');
      } finally {
        setLoading(false);
      }
    };

    fetchBrokers();
  }, []);

  // Split brokers into active and non-active groups
  const { activeBrokers, inactiveBrokers } = useMemo(() => {
    const active = allBrokers.filter((broker) => broker.is_active);
    const inactive = allBrokers.filter((broker) => !broker.is_active);
    return { activeBrokers: active, inactiveBrokers: inactive };
  }, [allBrokers]);

  // Filter brokers based on search
  const filterBrokers = (brokers: BrokerMetadata[]) => {
    return brokers.filter((broker) => {
      // Search filter - search in display name, broker type, and description
      const searchLower = searchText.toLowerCase();
      const displayName = (broker.display_name || '').toLowerCase();
      const brokerType = (broker.broker_type || '').toLowerCase();
      const description = (broker.description || '').toLowerCase();
      const markets = (broker.supported_markets || []).join(' ').toLowerCase();
      const matchesSearch =
        !searchText ||
        displayName.includes(searchLower) ||
        brokerType.includes(searchLower) ||
        description.includes(searchLower) ||
        markets.includes(searchLower);

      return matchesSearch;
    });
  };

  const filteredActiveBrokers = useMemo(() => filterBrokers(activeBrokers), [activeBrokers, searchText]);
  const filteredInactiveBrokers = useMemo(() => filterBrokers(inactiveBrokers), [inactiveBrokers, searchText]);

  const handleClearSearch = () => {
    setSearchText('');
  };

  const handleClearFilters = () => {
    setSearchText('');
  };

  const handleBrokerSelect = (broker: BrokerMetadata) => {
    navigate(`/broker-accounts/add/${broker.broker_type}`);
  };

  if (loading) {
    return (
      <AuthenticatedLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {allBrokers.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 4,
              border: '1px solid rgba(4, 55, 123, 0.1)',
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(4, 55, 123, 0.02) 0%, rgba(4, 55, 123, 0.05) 100%)',
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap">
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', flexGrow: 1 }}>
                <TextField
                  placeholder="Search brokers by name"
                  value={searchText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
                  size="small"
                  sx={{
                    flexGrow: 1,
                    maxWidth: { xs: '100%', sm: 450 },
                    '& .MuiInputBase-root': {
                      height: '40px',
                      fontSize: '0.875rem',
                      bgcolor: 'background.paper',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      py: '8px',
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ fontSize: '1.125rem', color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchText && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          edge="end"
                          sx={{ p: 0.5 }}
                        >
                          <ClearIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                {searchText && (
                  <Button
                    size="small"
                    onClick={handleClearFilters}
                    startIcon={<ClearIcon sx={{ fontSize: '0.875rem' }} />}
                    sx={{
                      whiteSpace: 'nowrap',
                      fontSize: '0.8125rem',
                      height: '40px',
                      px: 2,
                    }}
                  >
                    Clear Search
                  </Button>
                )}
              </Box>
            </Stack>
          </Paper>
        )}

        {allBrokers.length === 0 ? (
          <Card elevation={0} sx={{ border: '1px solid rgba(4, 55, 123, 0.1)', borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                No Brokers Available
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '500px', mx: 'auto' }}>
                We're currently setting up broker integrations. Please check back later or contact our support team for assistance.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Active Brokers Section */}
            {filteredActiveBrokers.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 0.5,
                      color: '#04377b',
                      fontSize: '1.5rem',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Supported Brokers
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                    }}
                  >
                    Click on any broker below to connect your account and start trading
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(3, 1fr)',
                      lg: 'repeat(4, 1fr)',
                      xl: 'repeat(5, 1fr)',
                    },
                    gap: { xs: 2, sm: 2.5, md: 3, lg: 3, xl: 3.5 },
                    width: '100%',
                    maxWidth: '100%',
                  }}
                >
                  {filteredActiveBrokers.map((broker) => {
                    const logoUrl = getBrokerLogoUrl(broker);
                    return (
                      <Box
                        key={broker.id || broker.broker_type}
                        onClick={() => handleBrokerSelect(broker)}
                        sx={{
                          cursor: 'pointer',
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.25,
                          p: 1.5,
                          minHeight: 100,
                          borderRadius: 3,
                          background: 'linear-gradient(180deg, #fff8ef 0%, #f5f0e8 100%)',
                          border: 'none',
                          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: 'linear-gradient(90deg, #04377b 0%, #0a5cb8 100%)',
                            transform: 'scaleX(0)',
                            transformOrigin: 'left',
                            transition: 'transform 0.3s ease',
                          },
                          '&:hover': {
                            borderColor: '#04377b',
                            transform: 'translateY(-6px)',
                            boxShadow: '0 12px 32px rgba(4, 55, 123, 0.15), 0 4px 16px rgba(4, 55, 123, 0.1)',
                            '&::before': {
                              transform: 'scaleX(1)',
                            },
                            '& .broker-logo': {
                              transform: 'scale(1.05)',
                            },
                            '& .broker-name': {
                              color: '#04377b',
                            },
                          },
                          '&:active': {
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        {logoUrl ? (
                          <Box
                            className="broker-logo"
                            sx={{
                              width: 100,
                              height: 100,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 0.5,
                              mb: 0,
                              transition: 'transform 0.3s ease',
                              '& img': {
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                              },
                            }}
                          >
                            <Box
                              component="img"
                              src={logoUrl}
                              alt={broker.display_name || broker.broker_type}
                              sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.12))',
                              }}
                            />
                          </Box>
                        ) : (
                          <Avatar
                            className="broker-logo"
                            sx={{
                              width: 100,
                              height: 100,
                              bgcolor: 'rgba(4, 55, 123, 0.08)',
                              color: '#04377b',
                              fontSize: '2.5rem',
                              fontWeight: 700,
                              transition: 'transform 0.3s ease',
                            }}
                            variant="rounded"
                          >
                            {(broker.display_name || broker.broker_type).charAt(0).toUpperCase()}
                          </Avatar>
                        )}
                        <Box sx={{ textAlign: 'center', mt: -1 }}>
                          {broker.is_beta && (
                            <Chip
                              label="Beta"
                              size="small"
                              color="warning"
                              sx={{
                                fontSize: '0.625rem',
                                height: '14px',
                                mt: 0.25,
                                fontWeight: 500,
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Inactive Brokers Section */}
            {filteredInactiveBrokers.length > 0 && (
              <Box sx={{ mb: 5 }}>
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      mb: 0.5,
                      color: '#666',
                      fontSize: '1.5rem',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Coming Soon
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                    }}
                  >
                    These brokers are currently being onboarded and will be available soon
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(3, 1fr)',
                      lg: 'repeat(4, 1fr)',
                      xl: 'repeat(5, 1fr)',
                    },
                    gap: { xs: 2, sm: 2.5, md: 3, lg: 3, xl: 3.5 },
                    width: '100%',
                    maxWidth: '100%',
                  }}
                >
                  {filteredInactiveBrokers.map((broker) => {
                    const logoUrl = getBrokerLogoUrl(broker);
                    return (
                      <Box
                        key={broker.id || broker.broker_type}
                        sx={{
                          cursor: 'not-allowed',
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.25,
                          p: 1.5,
                          minHeight: 100,
                          borderRadius: 3,
                          background: '#e2e2e2',
                          border: 'none',
                          opacity: 0.6,
                          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                          overflow: 'hidden',
                        }}
                      >
                        {logoUrl ? (
                          <Box
                            className="broker-logo"
                            sx={{
                              width: 100,
                              height: 100,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 0.5,
                              mb: 0,
                              transition: 'transform 0.3s ease',
                              '& img': {
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                filter: 'grayscale(100%)',
                              },
                            }}
                          >
                            <Box
                              component="img"
                              src={logoUrl}
                              alt={broker.display_name || broker.broker_type}
                              sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.12)) grayscale(100%)',
                              }}
                            />
                          </Box>
                        ) : (
                          <Avatar
                            className="broker-logo"
                            sx={{
                              width: 100,
                              height: 100,
                              bgcolor: 'rgba(0, 0, 0, 0.08)',
                              color: '#666',
                              fontSize: '2.5rem',
                              fontWeight: 700,
                              transition: 'transform 0.3s ease',
                            }}
                            variant="rounded"
                          >
                            {(broker.display_name || broker.broker_type).charAt(0).toUpperCase()}
                          </Avatar>
                        )}
                        <Box sx={{ textAlign: 'center', mt: -1 }}>
                          {broker.is_beta && (
                            <Chip
                              label="Beta"
                              size="small"
                              color="default"
                              sx={{
                                fontSize: '0.625rem',
                                height: '14px',
                                mt: 0.25,
                                fontWeight: 500,
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* No results message */}
            {filteredActiveBrokers.length === 0 && filteredInactiveBrokers.length === 0 && (
              <Card elevation={0} sx={{ border: '1px solid rgba(4, 55, 123, 0.1)', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                    No Brokers Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '500px', mx: 'auto' }}>
                    No brokers match your search criteria. Try adjusting your search terms or clear the filters to see all available brokers.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    startIcon={<ClearIcon />}
                    sx={{ mt: 1 }}
                  >
                    Clear Search
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}

      </Container>
    </AuthenticatedLayout>
  );
}
